// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// =============================================================================
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { acceptCgu, createPortalSession, getCgvVersions } from '../services/api';
import { gereSonAbonnement } from '../lib/acces';

/**
 * Acceptation des CGU/CGV — fenêtre qu'on ne peut pas fermer.
 *
 * Deux usages, UN composant (chantier SaaS 1ter, 24/09/2026) :
 *
 * - **au login** (`Login.js`), avec `jeton` et `utilisateur` : la session
 *   n'est pas encore ouverte, le jeton attend l'acceptation ;
 * - **en cours de session** (`AppLayout.js`), sans props : la barre latérale
 *   relit `/api/auth/me` toutes les 60 s ; le jour où une nouvelle version
 *   entre en vigueur, `cgu_accepted` y devient faux et la fenêtre s'ouvre, sans
 *   attendre la prochaine connexion.
 *
 * ⚠️ CE N'EST PAS LA SERRURE. Le serveur bloque les analyses tant que la
 * version en vigueur n'est pas acceptée (`_etat_droits_compte`,
 * `cgv_a_accepter`). La fenêtre n'est que le chemin le plus court vers la clé.
 *
 * Refus (décision de Marc, 23/09/2026) : pas d'accès. Qui gère un abonnement
 * payant est conduit au portail Stripe pour résilier (seule sortie utile), puis
 * déconnecté ; un siège de licence, ou un compte sans forfait, est déconnecté
 * avec un message — il n'a rien à résilier lui-même.
 *
 * Pendant un préavis, les DEUX versions sont présentées et le serveur
 * enregistre la version à venir (`core/cgv.version_a_enregistrer`) : pas de
 * seconde fenêtre le jour de l'entrée en vigueur.
 */

// `YYYY-MM-DD` ➜ date lisible dans la locale du navigateur (donnée, pas texte).
const dateLisible = (iso) => {
  const [a, m, j] = iso.split('-');
  return new Date(Number(a), Number(m) - 1, Number(j)).toLocaleDateString();
};

// Au login, `utilisateur` est le compte pas encore publié dans le contexte ; la
// session, elle, est déjà ouverte (cookies posés par le serveur, chantier ⑯).
const AcceptationCgv = ({ utilisateur = null, onAcceptee = null }) => {
  const { user, refreshUser, logout } = useAuth();
  const { t } = useI18n();
  const compte = utilisateur || user;
  const [calendrier, setCalendrier] = useState(null);
  const [coche, setCoche] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [refuse, setRefuse] = useState(false);

  const ouverte = !!compte && compte.cgu_accepted === false;

  useEffect(() => {
    if (!ouverte) return;
    getCgvVersions().then(setCalendrier).catch(() => setCalendrier(null));
  }, [ouverte]);

  if (!ouverte) return null;

  const accepter = async () => {
    setErreur('');
    setEnvoi(true);
    try {
      await acceptCgu();
      if (onAcceptee) onAcceptee();
      else await refreshUser();
    } catch (err) {
      setErreur(err.message || t('err_generic'));
    } finally {
      setEnvoi(false);
    }
  };

  const peutResilier = gereSonAbonnement(compte) && !!compte.tier_code;

  const refuser = async () => {
    setErreur('');
    if (!peutResilier) { setRefuse(true); return; }
    setEnvoi(true);
    try {
      // ⚠️ La route rend `portal_url` (cf Settings.js). Une première écriture
      // lisait `url` : `undefined`, et le navigateur partait sur /undefined (404).
      const { portal_url } = await createPortalSession();
      if (!portal_url) throw new Error('portal_url absente');
      // ATTENDUE : la navigation qui suit interromprait la fermeture de session.
      await logout();
      window.location.href = portal_url;
    } catch (_) {
      // Pas de client Stripe (palier posé à la main), paiements non configurés… :
      // on ne laisse pas le client sans issue, on lui dit à qui écrire.
      setErreur(t('cgv_acc_erreur_portail'));
      setRefuse(true);
      setEnvoi(false);
    }
  };

  const seDeconnecter = async () => {
    await logout();
    window.location.href = '/login';
  };

  const enVigueur = calendrier?.en_vigueur;
  const aVenir = calendrier?.a_venir;
  const remplir = (cle, version) =>
    t(cle).replace('{v}', version.numero).replace('{date}', dateLisible(version.en_vigueur_le));
  const resume = (aVenir || enVigueur)?.resume;

  const bouton = (principal) => ({
    padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: principal ? 'none' : '1px solid var(--gray)',
    background: principal ? 'var(--blue)' : 'transparent',
    color: principal ? 'white' : 'var(--black)',
  });

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="acceptation-cgv-titre"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface, white)', borderRadius: 12, padding: 24, width: 480, maxWidth: '90vw', boxShadow: 'var(--shadow-lg)' }}>
        <div id="acceptation-cgv-titre" style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
          {refuse ? t('cgv_acc_refus_titre') : t('cgv_acc_titre')}
        </div>

        {refuse ? (
          <>
            <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-muted)' }}>
              {compte.license_id || compte.license ? t('cgv_acc_refus_siege') : t('cgv_acc_refus_individuel')}
            </p>
            {erreur && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{erreur}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" onClick={seDeconnecter} style={bouton(true)}>{t('cgv_acc_deconnecter')}</button>
            </div>
          </>
        ) : (
          <>
            {enVigueur && (
              <p style={{ fontSize: 13, lineHeight: 1.5, margin: '0 0 8px' }}>{remplir('cgv_acc_intro', enVigueur)}</p>
            )}
            {aVenir && (
              <p style={{ fontSize: 13, lineHeight: 1.5, margin: '0 0 8px' }}>{remplir('cgv_acc_intro_a_venir', aVenir)}</p>
            )}
            {resume && (
              <p style={{ fontSize: 13, lineHeight: 1.5, margin: '0 0 8px', color: 'var(--text-muted)' }}>
                <strong>{t('cgv_acc_ce_qui_change')}</strong> {resume}
              </p>
            )}
            <p style={{ fontSize: 13, margin: '0 0 8px' }}>
              <a href="/cgv" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)' }}>{t('cgv_acc_lire_cgv')}</a>
              {' · '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)' }}>{t('cgv_acc_lire_privacy')}</a>
            </p>
            <p style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)', margin: '0 0 12px' }}>{t('cgv_acc_aide_decision')}</p>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={coche} onChange={e => setCoche(e.target.checked)} style={{ marginTop: 3 }} />
              <span>{t('cgv_acc_case')}</span>
            </label>

            {erreur && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{erreur}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button type="button" onClick={refuser} disabled={envoi} style={bouton(false)}>
                {peutResilier ? t('cgv_acc_refuser_resilier') : t('cgv_acc_refuser')}
              </button>
              <button type="button" onClick={accepter} disabled={!coche || envoi}
                style={{ ...bouton(true), opacity: (!coche || envoi) ? 0.6 : 1 }}>
                {envoi ? t('cgv_acc_en_cours') : t('cgv_acc_accepter')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AcceptationCgv;
